import type { GridColumn } from '@/components/grid'
import { parseAmount } from '@/features/cash/item-form'
import {
  emptyManualItemValues,
  manualItemBody,
  manualItemSchema,
  manualItemValues,
  type ManualItemFormValues,
} from '@/features/cash/manual-item-form'
import { previewPaymentDate } from '@/lib/cash'
import { formatDateOnly, formatDateTime, formatDecimal } from '@/lib/format'
import { messages } from '@/lib/messages'
import type { CashManualBulkRow, CashManualItem, CashSettings } from '@/services/http/cash'

const t = messages.cash.manual
export const INSTRUMENTS = ['', 'Banka', 'Kasa', 'Çek/senet ciro', 'Kendi çekimiz']

/**
 * `/nakit/erp-disi` ızgarasının satırı (docs/CRUD-ALTYAPI-PLAN.md Faz 3). Değerler editörün form
 * değerleridir (metin; `manual-item-form.ts`), böylece istemci doğrulaması ve gövde aynı kalır.
 * `key` kararlı satır kimliğidir (kayıtlı kalemde `id`, yeni satırda yerel); `version` okunan sürüm.
 */
export interface ManualGridRow extends ManualItemFormValues {
  key: string
  id: string | null
  version: number | null
}

/** Sunucu/şema alanı → ızgara kolonu: dönem ve tarih tek "Dönem / tarih" kolonunda. */
export const MANUAL_GRID_FIELDS: Partial<Record<string, string>> = { period: 'when', date: 'when' }

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** Izgara şeması: editörün şeması + dönem/tarih metni çözülebilmiş olmalı. */
export const manualGridSchema = manualItemSchema.superRefine((values, ctx) => {
  const field = values.source === 'duzenli' ? 'period' : 'date'
  const text = values[field]
  if (text && !ISO_DAY.test(text)) {
    ctx.addIssue({ code: 'custom', path: [field], message: t.grid.whenInvalid })
  }
})

let localKey = 0
function newKey(): string {
  localKey += 1
  return `yeni-${localKey}`
}

export function gridRowFromItem(item: CashManualItem): ManualGridRow {
  return { ...manualItemValues(item), key: item.id, id: item.id, version: item.version }
}

/**
 * Yeni satır: ilk tür, düzenli kaynak ve (verildiyse) planın başladığı ay. Dönem önceden dolar ki
 * Excel'den yalnız açıklama + tutar yapıştırılan satırlar kaydedilebilsin; hücrede değiştirilebilir.
 */
export function blankGridRow(type: string, period = ''): ManualGridRow {
  return { ...emptyManualItemValues(type), period, key: newKey(), id: null, version: null }
}

/** Dönem hazır dolduğu için içerik sayılmaz. */
const CONTENT_FIELDS = ['description', 'date', 'day', 'amount', 'cari_code', 'note']

/** Yeni ve hiçbir içerik alanı dolmamış satır kaydedilmez, doğrulanmaz. */
export function isBlankRow(row: ManualGridRow): boolean {
  return (
    row.id === null &&
    CONTENT_FIELDS.every((f) => !String(row[f as keyof ManualGridRow] ?? '').trim())
  )
}

const VALUE_FIELDS: Array<keyof ManualItemFormValues> = [
  'source',
  'type',
  'description',
  'instrument',
  'day',
  'period',
  'date',
  'amount',
  'cari_code',
  'status',
  'note',
]

/** Kayıtlı satır yüklenen hâlinden farklı mı (yeni satır boş değilse hep kirli). */
export function isRowDirty(row: ManualGridRow, original: ManualGridRow | undefined): boolean {
  if (row.id === null || !original) return !isBlankRow(row)
  return VALUE_FIELDS.some((f) => row[f] !== original[f])
}

/** Toplu kaydet gövdesinin satırı; güncellemede okunan sürüm gider. */
export function bulkRow(firm: string, row: ManualGridRow): CashManualBulkRow {
  const { firm: _firm, ...body } = manualItemBody(firm, row)
  return row.id === null ? body : { ...body, id: row.id, version: row.version ?? 0 }
}

function pad(n: string): string {
  return n.padStart(2, '0')
}

/** "20.11.2026", "20/11/2026", "2026-11-20" → ISO gün; değilse `null`. */
export function parseDay(text: string): string | null {
  const s = text.trim()
  let m = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(s)
  if (m) return validIso(`${m[3]}-${pad(m[2]!)}-${pad(m[1]!)}`)
  m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s)
  if (m) return validIso(`${m[1]}-${pad(m[2]!)}-${pad(m[3]!)}`)
  return null
}

/** "11.2026", "2026-11", tam tarih → o ayın ilk günü (ISO); değilse `null`. */
export function parseMonth(text: string): string | null {
  const s = text.trim()
  const day = parseDay(s)
  if (day) return `${day.slice(0, 7)}-01`
  let m = /^(\d{1,2})[./-](\d{4})$/.exec(s)
  if (m) return validIso(`${m[2]}-${pad(m[1]!)}-01`)
  m = /^(\d{4})-(\d{1,2})$/.exec(s)
  if (m) return validIso(`${m[1]}-${pad(m[2]!)}-01`)
  return null
}

function validIso(iso: string): string | null {
  const [y, mo, d] = iso.split('-').map(Number) as [number, number, number]
  const date = new Date(Date.UTC(y, mo - 1, d))
  return date.getUTCMonth() === mo - 1 && date.getUTCDate() === d ? iso : null
}

/** Dönem "11.2026", tarih "20.11.2026"; çözülemeyen ham metin olduğu gibi. */
export function whenText(row: ManualGridRow): string {
  if (row.source === 'duzenli') {
    if (!ISO_DAY.test(row.period)) return row.period
    const [y, m] = row.period.split('-')
    return `${m}.${y}`
  }
  return ISO_DAY.test(row.date) ? formatDateOnly(row.date) : row.date
}

function setWhen(row: ManualGridRow, text: string): ManualGridRow {
  if (row.source === 'duzenli')
    return { ...row, period: text.trim() ? (parseMonth(text) ?? text) : '' }
  return { ...row, date: text.trim() ? (parseDay(text) ?? text) : '' }
}

/** Kaynak değişince dönem/tarih korunur: tarih → ayı, dönem → (gün bilinmediği için) boş. */
function setSource(row: ManualGridRow, value: string): ManualGridRow {
  const source = value === 'tek' ? 'tek' : value === 'duzenli' ? 'duzenli' : null
  if (source === null || source === row.source) return row
  if (source === 'duzenli') {
    const period = ISO_DAY.test(row.date) ? `${row.date.slice(0, 7)}-01` : row.period
    return { ...row, source, period, date: '' }
  }
  return { ...row, source, date: row.date, day: '' }
}

function text<K extends keyof ManualGridRow>(field: K) {
  return {
    getText: (row: ManualGridRow) => String(row[field] ?? ''),
    setText: (row: ManualGridRow, value: string) => ({ ...row, [field]: value }),
  }
}

/**
 * Kolonlar; kimlik sabit kalsın diye ekran `useMemo` ile kurar. Açıklama ve tutar yan yanadır:
 * Excel'den bu iki sütun birlikte yapıştırılır (S3).
 */
export function manualGridColumns(
  settings: CashSettings,
  start: string,
  items: ReadonlyMap<string, CashManualItem>,
): GridColumn<ManualGridRow>[] {
  const c = t.columns
  return [
    {
      id: 'source',
      header: c.source,
      width: 110,
      kind: 'select',
      options: () => ['duzenli', 'tek'].map((v) => ({ value: v, label: t.sources[v]! })),
      getText: (row) => row.source,
      setText: setSource,
    },
    {
      id: 'type',
      header: c.type,
      width: 150,
      kind: 'select',
      options: () => settings.types.map((x) => ({ value: x.name, label: x.name })),
      ...text('type'),
    },
    { id: 'description', header: c.description, width: 220, kind: 'text', ...text('description') },
    {
      id: 'amount',
      header: c.amount,
      width: 130,
      kind: 'number',
      align: 'right',
      ...text('amount'),
      render: (row) => {
        const value = parseAmount(row.amount)
        return Number.isFinite(value) ? formatDecimal(value) : row.amount
      },
    },
    {
      id: 'when',
      header: c.period,
      width: 110,
      kind: 'text',
      getText: whenText,
      setText: setWhen,
    },
    {
      id: 'day',
      header: c.day,
      width: 56,
      kind: 'number',
      align: 'right',
      readOnly: (row) => row.source !== 'duzenli',
      ...text('day'),
    },
    {
      id: 'instrument',
      header: c.instrument,
      width: 130,
      kind: 'select',
      options: () => INSTRUMENTS.map((v) => ({ value: v, label: v || 'Banka' })),
      ...text('instrument'),
    },
    {
      id: 'payment',
      header: c.payment,
      width: 110,
      kind: 'readonly',
      getText: (row) => paymentDate(row, settings, start) ?? '',
      setText: (row) => row,
      render: (row) => {
        const payment = paymentDate(row, settings, start)
        if (payment === undefined) return ''
        return payment ? formatDateOnly(payment) : t.grid.paidBefore
      },
    },
    { id: 'cari_code', header: c.cariCode, width: 110, kind: 'text', ...text('cari_code') },
    { id: 'note', header: c.note, width: 180, kind: 'text', ...text('note') },
    {
      id: 'updated',
      header: c.updated,
      width: 170,
      kind: 'readonly',
      getText: (row) => updatedText(row.id ? items.get(row.id) : undefined),
      setText: (row) => row,
    },
  ]
}

/** Hesaplanan ödeme günü: `undefined` = hesaplanamaz, `null` = başlangıçtan önce (ödenmiş). */
function paymentDate(
  row: ManualGridRow,
  settings: CashSettings,
  start: string,
): string | null | undefined {
  if (row.source === 'tek') return ISO_DAY.test(row.date) ? row.date : undefined
  if (!ISO_DAY.test(row.period)) return undefined
  const day = Number(row.day)
  const type = settings.types.find((x) => x.name === row.type)
  return previewPaymentDate(
    row.period,
    row.day.trim() && Number.isInteger(day) ? day : null,
    type,
    start,
  )
}

function updatedText(item: CashManualItem | undefined): string {
  if (!item) return ''
  return item.updated_by_name
    ? messages.cash.planning.lastChange(item.updated_by_name, formatDateTime(item.updated_at))
    : formatDateTime(item.updated_at)
}
